import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import tshirt from '../../assets/images/cat-fashion.jpg';
import cap from '../../assets/images/cap.jpg';
import backpack from '../../assets/images/bag.jpg';
import homeHero from '../../assets/images/F&A.jpg';
import { fetchProductListBasedOnCategory } from '../../redux/slices/client/clientProductSlice';
import burshIcon from '../../assets/images/brush_icon.png';
import { useLocation } from 'react-router-dom';
import StickyCategoryCouponWidget from '../../components/common/widgets/StickyCategoryCouponWidget';
// Shimmer component for products
const ProductShimmer = () => (
  <div className="group relative bg-gray-200 rounded-lg overflow-hidden animate-pulse">
    <div className="w-full h-64 bg-gray-300"></div>
  </div>
);

// Shimmer component for categories
const CategoryShimmer = () => (
  <div className="bg-gray-200 p-4 rounded-md shadow-sm text-center animate-pulse">
    <div className="h-6 bg-gray-300 rounded mb-2"></div>
    <div className="h-4 bg-gray-300 rounded"></div>
  </div>
);

const ProductSections = ({ productCategories, categoryID, loading }) => {
  const navigate = useNavigate();
  if (loading) {
    // Render shimmer effect while loading
    return (
      <div className="max-w-screen-xl mx-auto w-full px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <ProductShimmer key={index} />
          ))}
        </div>
        <div className="mt-16">
          <h2 className="text-2xl font-medium text-gray-800 mb-8 text-center">
            Other Categories
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, index) => (
              <CategoryShimmer key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (Object.keys(productCategories).length === 0) {
    return null;
  }

  // Separate categories into two groups: those with products and those without
  const categoriesWithProducts = [];
  const categoriesWithoutProducts = [];

  Object.entries(productCategories).forEach(([categoryName, products]) => {
    const productDetail = products.data || []; // Ensure productDetail is an array
    const SubcategoryID = products.meta.id; // Assuming this is the ID you want to collect

    if (productDetail.length > 0) {
      categoriesWithProducts.push({ categoryName, productDetail, SubcategoryID });
    } else {
      categoriesWithoutProducts.push({ categoryName, productDetail, SubcategoryID });
    }
  });

  return (
    <div className="max-w-screen-xl mx-auto w-full px-4 py-8">
      {/* First render categories with products */}
      {categoriesWithProducts.map(({ categoryName, productDetail: products, SubcategoryID }) => (
        <section key={categoryName} className="mb-16">
          <h2 className="text-2xl font-medium text-gray-800 mb-8 text-start py-4">
            {categoryName}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.slice(0, 8).map((product) => (
              <div
                key={product.id}
                className="group relative bg-white rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg"
              >
                <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {product.stock > 0 ? (
                    <button
                      className="text-white px-6 py-3 rounded-full hover:opacity-90 transition-all transform group-hover:scale-105"
                      style={{ backgroundColor: '#0B0C52' }}
                      onClick={() => navigate(`/design/${SubcategoryID}`)}
                    >
                      Customize Now
                      <span className="ml-2">
                        <img src={burshIcon} alt="Brush Icon" className="inline-block w-4 h-4" />
                      </span>
                    </button>
                  ) : (
                    <button
                      className="text-gray-400 px-6 py-3 rounded-full cursor-not-allowed"
                      style={{ backgroundColor: '#CCCCCC' }}
                      disabled
                    >
                      Out of Stock
                    </button>
                  )}
                </div>
                <img
                  src={product.images.front_image}
                  alt={product.product_name}
                  className="w-full h-64 object-cover group-hover:opacity-80 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to={`detail/${SubcategoryID}`}
              className="inline-block px-6 py-3 rounded-md shadow-md transition-all duration-300 hover:shadow-lg hover:transform hover:scale-105 text-sm font-semibold text-[#0B0C52] underline"

            >

              Browse All {categoryName} Templates &rarr;
            </Link>
          </div>


        </section>
      ))}

      {/* Then render categories without products grouped together */}
      {categoriesWithoutProducts.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-medium text-gray-800 mb-8 text-center">
            Other Categories
          </h2>
          <div className="bg-[#D1D5DB] rounded-lg p-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4">
              Categories currently without products:
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categoriesWithoutProducts.map(({ categoryName }) => (
                <div
                  key={categoryName}
                  className="bg-white p-4 rounded-md shadow-sm text-center"
                >
                  <p className="text-gray-600">{categoryName}</p>
                  <p className="text-sm text-gray-400 mt-1">No products available yet</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const FashionAccessories = () => {
  let { id, productCategoryName } = useParams();
  const { error, loading, categories } = useSelector((state) => state.home);
  const { error: productError, loadingCategory: productLoading, productsCategories } = useSelector((state) => state.productByCategory);
  const dispatch = useDispatch();
  const location = useLocation();

  // Access the 'from' state passed from the previous page
  const from = location.state?.from;


  useEffect(() => {
    dispatch(fetchProductListBasedOnCategory(id));
  }, [dispatch, id]);

  if (!productCategoryName) {
    const matchingCategory = categories.find((category) => category.id == id);
    if (matchingCategory) {
      productCategoryName = matchingCategory.name;
    }
  }

  const formattedName = productCategoryName
    ? productCategoryName.replace(/-/g, " ").replace(/\s+/g, ' ').trim()
    : '';

  return (
    <div className="bg-[#F6F7F9] min-h-screen pb-12">
      {/* HERO SECTION */}
      <div
        className="min-h-[70vh] bg-cover bg-center flex flex-col justify-center items-center text-center py-16 px-5"
        style={{ backgroundImage: `url(${homeHero})` }}
      >
        <h1 className="text-white font-bold text-6xl md:text-[100px] mb-6">
          Create Your Own Style – Custom Hoodies, Caps & More!
        </h1>

      </div>

      {/* Breadcrumb */}
      <div className="bg-[#0B0C52] mx-auto w-full px-4 pt-6 text-sm text-white width-full">
        <nav className='align-center'>
          <Link to={from ? from : '/'}>{from ? 'Categories' : 'Home'}</Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-[#FFD700]">{formattedName}</span>
        </nav>
      </div>

      {/* Page Title */}
      <div className="max-w-screen-xl mx-auto w-full px-4 mt-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">
          {formattedName}
        </h1>
      </div>

      <ProductSections productCategories={productsCategories} categoryID={id} loading={loading || productLoading} />
      <StickyCategoryCouponWidget
        categoryId={id}
        categoryName={formattedName}
      />
    </div>
  );
};

export default FashionAccessories;